import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import clsx from 'clsx';
import {
  type ComponentProps,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import * as styles from './animated-region.css';

export interface AnimatedRegionProps extends Omit<
  ComponentProps<'div'>,
  'hidden' | 'ref'
> {
  expanded: boolean;
  keepMounted?: boolean;
  animate?: boolean;
  animateContent?: boolean;
  /**
   * With `animateContent`: while the content shows a loader (an element with
   * `aria-busy="true"`) the region keeps the height it had, and grows or
   * shrinks to the loaded content in one motion once it is there.
   */
  holdWhileBusy?: boolean;
  preserveHeightDuringCollapse?: boolean;
  returnFocusRef?: RefObject<HTMLElement | null>;
  onSettled?: (expanded: boolean) => void;
}

/** Lazy first render, stable children thereafter, natural layout after motion. */
export function AnimatedRegion({
  expanded,
  keepMounted = false,
  animate = true,
  animateContent = false,
  holdWhileBusy = false,
  preserveHeightDuringCollapse = false,
  returnFocusRef,
  onSettled,
  children,
  className,
  ...props
}: AnimatedRegionProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const previousExpanded = useRef<boolean | undefined>(undefined);
  const expandedHeight = useRef<number | undefined>(undefined);
  const [visited, setVisited] = useState(expanded);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    if (expanded) setVisited(true);
    const previous = previousExpanded.current;
    previousExpanded.current = expanded;
    if (previous === undefined || !animate) {
      streamLainAnimationController.settleRegion(element, expanded);
      return;
    }
    if (previous === expanded) return;
    if (!expanded && element.contains(element.ownerDocument.activeElement)) {
      returnFocusRef?.current?.focus();
    }
    // Child layout effects can register rows and replace their empty state.
    // Measure after that commit settles, before the browser paints.
    let disposed = false;
    queueMicrotask(() => {
      if (disposed) return;
      const handle = streamLainAnimationController.reveal(
        element,
        expanded,
        !expanded &&
          preserveHeightDuringCollapse &&
          expandedHeight.current !== undefined
          ? { previousHeight: expandedHeight.current, nextHeight: 0 }
          : undefined
      );
      void handle.finished.then(outcome => {
        if (outcome === 'finished' && expandedRef.current === expanded)
          onSettledRef.current?.(expanded);
      });
    });
    return () => {
      disposed = true;
    };
  }, [animate, expanded, preserveHeightDuringCollapse, returnFocusRef]);

  useLayoutEffect(() => {
    const element = elementRef.current;
    const Observer = element?.ownerDocument.defaultView?.ResizeObserver;
    if (!preserveHeightDuringCollapse || !element || !Observer) return;
    const measure = () => {
      if (expandedRef.current && !element.hidden)
        expandedHeight.current = element.getBoundingClientRect().height;
    };
    measure();
    const observer = new Observer(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [preserveHeightDuringCollapse]);

  useLayoutEffect(() => {
    const element = elementRef.current;
    return () => {
      if (element) streamLainAnimationController.cancel(element);
    };
  }, []);

  useLayoutEffect(() => {
    const element = elementRef.current;
    const content = contentRef.current;
    const Observer = element?.ownerDocument.defaultView?.ResizeObserver;
    if (!animateContent || !element || !content || !Observer) return;
    let previousHeight: number | undefined;
    const release = () => {
      element.style.minHeight = '';
    };
    const observer = new Observer(() => {
      if (!expandedRef.current || element.hidden) return;
      const height = content.offsetHeight;
      // A loader is smaller than what it loads. Holding the last height keeps
      // the page from collapsing to the loader and growing again right after.
      if (
        holdWhileBusy &&
        previousHeight !== undefined &&
        height < previousHeight &&
        content.querySelector('[aria-busy="true"]')
      ) {
        element.style.minHeight = `${previousHeight}px`;
        return;
      }
      release();
      // A nested reveal already animates natural layout. Retiming its height
      // on the parent causes reversals and a final snap to intrinsic height.
      const descendantResizing = content
        .getAnimations({ subtree: true })
        .some(animation => {
          const effect = animation.effect;
          return (
            effect instanceof KeyframeEffect &&
            effect.getKeyframes().some(frame => frame.height !== undefined)
          );
        });
      if (
        !descendantResizing &&
        previousHeight !== undefined &&
        Math.abs(height - previousHeight) > 1
      ) {
        streamLainAnimationController.resizeRegion(
          element,
          previousHeight,
          height
        );
      }
      previousHeight = height;
    });
    observer.observe(content);
    return () => {
      observer.disconnect();
      release();
    };
  }, [animateContent, holdWhileBusy]);

  return (
    <div {...props} className={clsx(styles.region, className)} ref={elementRef}>
      {animateContent ? (
        <div ref={contentRef} className={styles.content}>
          {keepMounted || expanded || visited ? children : null}
        </div>
      ) : keepMounted || expanded || visited ? (
        children
      ) : null}
    </div>
  );
}
